import React, { useState, useContext } from "react";
import { Controller } from "react-hook-form";
import { Grid, TextField } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputUI from "../material-ui/InputUI";
import { AuthContext } from "../../context/Auth/AuthContext";
import { RiCloseCircleFill } from "react-icons/ri";

const EditDocumentForm = ({
  register,
  control,
  errors,
  tableValues,
  setValue,
  userRolesOptions = [], // Passed from parent (fetched once on dialog open)
}) => {
  const [userType, setUserType] = useState("");
  const { is_admin } = useContext(AuthContext);
  const [isAdmin, setIsAdmin] = useState(tableValues?.is_admin);

  const roleOptions = [
    {
      name: "Admin",
      value: 1,
    },
    {
      name: "Non Admin",
      value: 0,
    },
  ];

  const statusOptions = [
    {
      name: "Active",
      value: "active",
    },
    {
      name: "Inactive",
      value: "inactive",
    },
  ];

  return (
    <Grid className="">
      <div className="edit-document-form grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="field_wrapper">
          <div className="field">
            <div className="input-field">
              <Controller
                name="firstName"
                defaultValue={tableValues.first_name}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputUI
                    name="firstName"
                    label="First Name"
                    type="text"
                    value={value ? value : ""}
                    setValue={onChange}
                    variant="outlined"
                    register={register}
                  />
                )}
              />
            </div>
            <span className="error-message">
              {errors["firstName"]?.message}
            </span>
          </div>
        </div>
        <div className="field_wrapper">
          <div className="field">
            <div className="input-field">
              <Controller
                name="lastName"
                defaultValue={tableValues.last_name}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputUI
                    name="lastName"
                    label="Last Name"
                    type="text"
                    value={value ? value : ""}
                    setValue={onChange}
                    variant="outlined"
                    register={register}
                  />
                )}
              />
            </div>
            <span className="error-message">{errors["lastName"]?.message}</span>
          </div>
        </div>

        <div className="field_wrapper">
          <div className="field" style={{ height: "85px" }}>
            <FormControl fullWidth>
              <InputLabel id="select-label">Status</InputLabel>
              <Controller
                control={control}
                name="status"
                defaultValue={tableValues.status.toLowerCase()}
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="status"
                    labelId="select-label"
                    variant="outlined"
                    id="simple-select"
                    value={value}
                    onChange={onChange}
                    label="Status"
                    {...register("status")}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <span className="error-message">{errors["status"]?.message}</span>
          </div>
        </div>

        <div className="field_wrapper">
          <div className="field" style={{ height: "85px" }}>
            <FormControl fullWidth>
              <InputLabel id="select-label">User Type</InputLabel>
              <Controller
                control={control}
                name="role"
                defaultValue={tableValues.is_admin}
                disabled={is_admin === "0"}
                render={({ field }) => (
                  <Select
                    {...field} // Spread field to bind value and onChange correctly
                    labelId="select-label"
                    variant="outlined"
                    id="simple-select"
                    onChange={(e) => {
                      const newValue = e.target.value.toString();
                      setIsAdmin(newValue);
                      field.onChange(e);

                      // if (newValue === "1") {
                      // Reset userRole to empty when switching to Admin
                      setValue("userRole", []);
                      // }
                    }}
                    label="User Type"
                  >
                    {roleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <span className="error-message">{errors["role"]?.message}</span>
          </div>
        </div>
      </div>
      {
        // Renders a multi-select dropdown for user roles with tags to display selected roles.
        // Allows removing roles via tag clicks. Visible only if the user is not an admin.
      }
      {isAdmin === "0" && (
        <>
          <div className="mb-4  w-full max-w-full">
            <div className="field" style={{ height: "85px" }}>
              <FormControl fullWidth>
                <InputLabel id="edit-doc-select-label">Roles</InputLabel>
                <Controller
                  control={control}
                  name="userRole"
                  defaultValue={
                    tableValues?.role_id ? tableValues?.role_id.split(",") : []
                  }
                  render={({ field }) => {
                    const { value, onChange } = field;
                    const selectedRoles = value || [];

                    // Handle dropdown selection
                    const handleSelectChange = (event) => {
                      onChange(event.target.value); // Update form state
                    };

                    // Remove a tag (also deselects from dropdown)
                    const removeRole = (roleId) => {
                      const updatedRoles = selectedRoles.filter(
                        (id) => id !== roleId
                      );
                      onChange(updatedRoles); // Update form state
                    };

                    return (
                      <>
                        <Select
                          name="userRole"
                          labelId="select-label"
                          variant="outlined"
                          id="simple-select"
                          multiple
                          value={selectedRoles}
                          onChange={handleSelectChange}
                          label="Roles"
                        >
                          {userRolesOptions.map((option) => (
                            <MenuItem
                              key={option?.role_id}
                              value={option?.role_id}
                            >
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>

                        {
                          // Displays selected roles as tags with a remove option.
                        }
                        {selectedRoles.length > 0 && (
                          <div className="flex flex-wrap gap-2 p-2 bg-gray-100 rounded-b-lg rounded-none border border-gray-300">
                            {selectedRoles.map((roleId, index) => {
                              const role = userRolesOptions.find(
                                (r) => r.role_id === roleId
                              );
                              return (
                                <div
                                  key={index}
                                  className=" flex items-center gap-1 bg-[#1a6b7c] text-white px-3 py-1 rounded-full text-sm shadow-md"
                                >
                                  <span className="text">{role?.name}</span>
                                  <span
                                    className="close cursor-pointer hover:text-red-400 transition"
                                    onClick={() => removeRole(roleId)}
                                  >
                                    <RiCloseCircleFill className="w-4 h-4" />
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </FormControl>
              <span className="error-message">
                {errors["userRole"]?.message}
              </span>
            </div>
          </div>
        </>
      )}
    </Grid>
  );
};

export default EditDocumentForm;
